import { useCallback, useEffect, useState } from 'react';
import { BuildLearn } from './features/BuildLearn';
import { ChordFIB } from './features/ChordFIB';
import { Learn } from './features/Learn';
import {
  audioStatus,
  getTimbre,
  onAudioStatusChange,
  prepareAudio,
  retryInstrument,
  setTimbre,
  unlock,
  type Timbre,
} from './audio/player';
import { INSTRUMENT_IDS, INSTRUMENTS } from './audio/instruments';
import { repositories } from './storage/repositories';

/** Where the learner's instrument choice lives, so it survives a relaunch. */
const SOUND_SETTING = 'sound';
import { BrandMark } from './design/BrandMark';
import { LaunchOverlay } from './design/LaunchOverlay';
import { Button } from './design/Button';
import { useAuth } from './auth/AuthProvider';
import { useRoute } from './auth/useRoute';
import { isAuthRoute } from './auth/routes';
import { SignIn } from './auth/SignIn';
import { CreateAccount } from './auth/CreateAccount';
import { VerifyEmail } from './auth/VerifyEmail';
import { ForgotPassword } from './auth/ForgotPassword';
import { ResetPassword } from './auth/ResetPassword';
import { AccountSettings } from './auth/AccountSettings';
import { AdoptLocalWork } from './auth/AdoptLocalWork';
import { ConflictNotice, SyncStatusLine } from './sync/SyncStatus';
import { Home } from './features/Home';
import { ModesLab } from './features/ModesLab';
import { IntervalsLab } from './features/IntervalsLab';
import { ChordLegend } from './features/ChordLegend';
import { Classroom } from './features/Classroom';
import { BottomNav, NavMenu } from './design/GlobalNav';
import type { BuildRequest, Destination } from './auth/routes';


export default function App() {
  const { route, navigate } = useRoute();
  const auth = useAuth();

  // Asked once per signed-in learner, and only when there is something to ask
  // about. `AdoptLocalWork` renders nothing until it knows, so the writing
  // surface never blanks while the question is being worked out.
  const [adoptionSettled, setAdoptionSettled] = useState(false);
  const settleAdoption = useCallback(() => setAdoptionSettled(true), []);

  // A different account gets asked about this iPad's work on its own terms.
  const userId = auth.state.user?.id ?? '';
  useEffect(() => {
    setAdoptionSettled(false);
  }, [userId]);

  if (isAuthRoute(route)) {
    switch (route.name) {
      case 'sign-in':
        return <SignIn navigate={navigate} />;
      case 'create-account':
        return <CreateAccount navigate={navigate} />;
      case 'verify-email':
        return <VerifyEmail route={route} navigate={navigate} />;
      case 'forgot-password':
        return <ForgotPassword navigate={navigate} />;
      case 'reset-password':
        return <ResetPassword route={route} navigate={navigate} />;
    }
  }

  if (route.name === 'account') {
    return <AccountSettings navigate={navigate} />;
  }

  const destination = route.name === 'app' ? route.destination : 'home';

  return (
    <>
      <WritingApp
        navigate={navigate}
        destination={destination}
        build={route.name === 'app' ? route.build : undefined}
      />
      {auth.state.status === 'signed-in' && !adoptionSettled ? (
        <div className="mm-auth-overlay">
          <AdoptLocalWork onDone={settleAdoption} />
        </div>
      ) : null}
    </>
  );
}

/**
 * The app proper — the writing surface and its three sections.
 *
 * Signed in or not, this is what a learner gets. Nothing below is gated on an
 * account: guest use is a first-class mode, so the only thing authentication
 * changes here is what the account control in the masthead says.
 */
function WritingApp({
  navigate,
  destination,
  build,
}: {
  navigate: ReturnType<typeof useRoute>['navigate'];
  destination: Destination;
  /** A chord the Chord Legend asked FIB to build. Read from the URL. */
  build?: BuildRequest;
}) {
  const [timbre, setTimbreState] = useState<Timbre>(getTimbre);
  /**
   * Go somewhere, optionally carrying a chord to build.
   *
   * The build request rides in the URL rather than in a variable, so pressing
   * Back after "Build in FIB" returns to the Legend and pressing Forward comes
   * back to the same chord. A hand-off held in memory would be consumed once
   * and then be gone.
   */
  const go = useCallback(
    (next: Destination, build?: BuildRequest) =>
      navigate(build ? { name: 'app', destination: next, build } : { name: 'app', destination: next }),
    [navigate],
  );
  /**
   * Write Chord FIB's selection into the address bar as it changes.
   *
   * Replaced, not pushed: adjusting the root is the same place, not a new one.
   * What this buys is the thing component state cannot — opening the Chord
   * Legend unmounts FIB, and the history entry the learner comes back to still
   * describes the chord they had set up.
   */
  const rememberFibState = useCallback(
    (nextBuild: BuildRequest) =>
      navigate({ name: 'app', destination: 'fib', build: nextBuild }, { replace: true }),
    [navigate],
  );

  const [status, setStatus] = useState(() => audioStatus());

  // iOS requires a user gesture before audio can start; take the first one we get.
  useEffect(() => {
    const handler = () => {
      void unlock();
    };
    window.addEventListener('pointerdown', handler, { once: true });
    return () => window.removeEventListener('pointerdown', handler);
  }, []);

  /**
   * Restore the chosen instrument before it is ever shown.
   *
   * The picker used to render Piano and then flip to Guitar once storage
   * answered, which reads as the app changing its mind about what you chose. The
   * flip is gone because the fetch runs before the first paint of the label and
   * only sets state if it found something different.
   */
  useEffect(() => {
    let cancelled = false;
    void repositories()
      .settings.get(SOUND_SETTING)
      .then((record) => {
        const saved = (record?.value as { timbre?: string } | undefined)?.timbre;
        if (cancelled || !saved) return;
        if (!INSTRUMENT_IDS.includes(saved as Timbre)) return;
        setTimbreState(saved as Timbre);
        setTimbre(saved as Timbre);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Start loading the moment the shell is up, not on the first tap.
   *
   * Decoding still waits for the iOS audio unlock, but the download does not
   * have to — and starting it here is what makes the first Listen able to *wait*
   * for the real instrument rather than substituting a different one.
   */
  useEffect(() => {
    prepareAudio(timbre);
  }, [timbre]);

  // Samples load in the background, so the label reflects what will actually sound.
  useEffect(() => onAudioStatusChange(() => setStatus(audioStatus())), []);

  return (
    <div className={`app ${destination === 'manuscript' ? 'is-manuscript' : ''}`}>
      {/* Over the app, never in front of it: everything below is mounted and
          painting from the first frame, so nothing waits on the opening. */}
      <LaunchOverlay />
      <SessionBanner navigate={navigate} />

      <header className="masthead">
        <button className="brand" type="button" onClick={() => go('home')} aria-label="MusicMentor home">
          <BrandMark size={38} />
          <div>
            <p className="brand-name">MusicMentor</p>
            <p className="brand-tag">Write. See. Hear.</p>
          </div>
        </button>
        <label className="sound-picker">
          <span className="sound-picker__label">Sound</span>
          <span className="sound-picker__control">
            <select
              value={timbre}
              aria-label="Instrument"
              onChange={(e) => {
                const next = e.target.value as Timbre;
                setTimbreState(next);
                setTimbre(next);
                void repositories().settings.set(SOUND_SETTING, { timbre: next });
              }}
            >
              {INSTRUMENT_IDS.map((id) => INSTRUMENTS[id]).map((instrument) => (
                <option key={instrument.id} value={instrument.id}>
                  {instrument.label}
                </option>
              ))}
            </select>
          {/* Learner language, never build instructions. "synth — run
              fetch-samples" was a message to a developer printed in a learner's
              interface, and it appeared on the ordinary path rather than only
              when something had actually failed. */}
            <span className={`sound-status is-${status}`} role="status">
              {status === 'ready'
                ? 'Ready'
                : status === 'loading'
                  ? 'Preparing…'
                  : status === 'degraded'
                    ? 'Basic sound'
                    : ''}
            </span>
          </span>
          {status === 'degraded' ? (
            <button
              type="button"
              className="mm-button mm-button--tertiary sound-retry"
              onClick={() => void retryInstrument(timbre)}
            >
              Retry
            </button>
          ) : null}
        </label>

        <div className="mm-account-entry-group">
          <SyncStatusLine />
          <AccountEntry navigate={navigate} />
        </div>

        {/* Both navigation surfaces read one destination and call one `go`, so
            they cannot disagree about where you are. */}
        <NavMenu current={destination} go={go} />
      </header>

      <main>
        {/* Above the work, not buried in a console: a conflict is the one thing
            here that cannot be resolved without a person. */}
        <div className="app-conflict">
          <ConflictNotice />
        </div>
        {destination === 'home' ? (
          <Home go={go} onContinue={() => go('write')} />
        ) : destination === 'write' || destination === 'manuscript' ? (
          <BuildLearn
            studio={destination === 'manuscript'}
            onOpenStudio={() => go('manuscript')}
            onCloseStudio={() => go('write')}
          />
        ) : destination === 'fib' ? (
          <ChordFIB
            build={build}
            onOpenLegend={() => go('chord-legend')}
            onStateChange={rememberFibState}
          />
        ) : destination === 'modes' ? (
          <ModesLab />
        ) : destination === 'intervals' ? (
          <IntervalsLab />
        ) : destination === 'chord-legend' ? (
          <ChordLegend go={go} />
        ) : destination === 'classroom' ? (
          <Classroom />
        ) : (
          <Learn />
        )}
      </main>

      <BottomNav current={destination} go={go} />

      <footer className="footer">
        <p>
          Recognition happens on this device. Guest work stays here; signed-in learners choose
          what to sync or share.
        </p>
      </footer>
    </div>
  );
}

/**
 * The account control.
 *
 * Deliberately quiet, and deliberately honest about not knowing. While the
 * session is still being restored it shows neither "Sign in" nor an account —
 * offering to sign in to somebody who is already signed in is worse than showing
 * nothing for a moment.
 */
function AccountEntry({ navigate }: { navigate: ReturnType<typeof useRoute>['navigate'] }) {
  const { state, restoring, reachable } = useAuth();

  if (restoring) {
    return (
      <div className="mm-account-entry">
        <span className="mm-account-entry__status">Checking your account…</span>
      </div>
    );
  }

  if (state.status === 'signed-in' || state.status === 'verification-required') {
    return (
      <div className="mm-account-entry">
        <Button variant="tertiary" onClick={() => navigate({ name: 'account' })}>
          Account
        </Button>
      </div>
    );
  }

  return (
    <div className="mm-account-entry">
      {!reachable ? (
        // Being offline is not being signed out, and the control should not
        // imply the app has decided anything about the account.
        <span className="mm-account-entry__status">Offline — working on this iPad</span>
      ) : null}
      <Button variant="tertiary" onClick={() => navigate({ name: 'sign-in' })}>
        Sign in
      </Button>
    </div>
  );
}

/**
 * The expired-session notice.
 *
 * A session ending is not a reason to take the staff away, so this is a banner
 * above the app rather than a redirect to a sign-in screen. The learner keeps
 * writing; their work keeps saving locally; they sign in again when they choose.
 */
function SessionBanner({ navigate }: { navigate: ReturnType<typeof useRoute>['navigate'] }) {
  const { sessionExpired, dismissExpiry } = useAuth();
  if (!sessionExpired) return null;

  return (
    <div className="mm-session-banner" role="status">
      <p className="mm-session-banner__message">
        You have been signed out. Your work is still saved on this iPad.
      </p>
      <div className="mm-session-banner__actions">
        <Button
          variant="secondary"
          onClick={() => {
            dismissExpiry();
            navigate({ name: 'sign-in' });
          }}
        >
          Sign in again
        </Button>
        <Button variant="tertiary" onClick={dismissExpiry}>
          Keep writing
        </Button>
      </div>
    </div>
  );
}
